<template>
    <span v-if="asiento?.es_hueco"
          class="butaca hueco"
          aria-hidden="true"
    ></span>
    <span v-else
          class="butaca"
          :class="getStatusClasses(asiento)"
          :data-row="fila"
          :data-seat="asiento.asiento"
          @click="handleClick" :title="getTitle(asiento, fila)"
          @contextmenu="showContextMenu"
          :style="getButacaStyle(asiento, disabled)"
    >
        <span style="pointer-events: none;">{{ asiento.asiento }}</span>
    </span>
</template>

<script>
    import { resolveConceptoBloqueo } from '@/utils/abonos/resolveConceptoBloqueo';

    export default {
        props: {
            asiento: Object,
            fila: [String, Number],
            disabled: Boolean,
            front: {
                type: Boolean,
                default: false
            },
            /**
             * Habilita la selección de butacas bloqueadas (ocupado === 3).
             *
             * La autorización es explícita y no por defecto: poder actuar sobre un
             * bloqueo es una capacidad de negocio del gestor Admin, no la política
             * del componente compartido. Sin esta prop sólo se aceptan butacas
             * libres (ocupado === 0), que es lo correcto para los flujos Front y
             * SGA, donde una butaca bloqueada nunca debe llegar a validarse.
             *
             * Se activa desde las vistas Admin que desbloquean butacas.
             */
            allowBlocked: {
                type: Boolean,
                default: false
            },
            // Sólo cambia el CURSOR de una butaca bloqueada, para que la UI no
            // contradiga una acción ya permitida. No concede permiso de
            // interacción: eso lo decide `allowBlocked`.
            bloqueadaPointerCursor: {
                type: Boolean,
                default: false
            }
        },
        methods: {
            /**
             * Indica si un asiento puede recibir interacción.
             *
             * Regla base (política por defecto del componente compartido): sólo
             * butacas libres (0). Quedan fuera ocupadas (1/true), pendientes (2),
             * reservadas (`info.reserva_id`) y ocultas.
             *
             * Las butacas bloqueadas (3 sin reserva) sólo son accionables cuando el
             * consumidor habilita `allowBlocked`, porque poder actuar sobre un
             * bloqueo es una capacidad del gestor Admin y no debe filtrarse a los
             * flujos Front/SGA.
             *
             * @param {Object} asiento
             * @returns {boolean}
             */
            isDisponible(asiento) {
                if (!asiento) return false;
                if (asiento.es_hueco) return false;
                if (asiento.oculto && asiento.oculto > 0) return false;
                if (asiento.ocupado === 1 || asiento.ocupado === true) return false;
                if (asiento.ocupado === 2) return false;
                if (asiento.info?.reserva_id) return false;

                if (asiento.ocupado === 0 || asiento.ocupado === false) return true;

                return this.allowBlocked && asiento.ocupado === 3;
            },
            handleClick() {
                if (!this.isDisponible(this.asiento)) {
                    return;
                }
                this.$emit('toggle');
            },
            getStatusClasses(asiento) {
                if(asiento.oculto > 0) {
                    return {
                        'seleccionada' : asiento.seleccionado,
                        'oculto': asiento.oculto === 1,
                        'columna': asiento.oculto == 2,
                        'columna_n': asiento.oculto == 3
                    };
                }

                if(this.front) {
                    return {
                        'ocupada' : asiento.ocupado,
                        'seleccionada' : asiento.seleccionado,
                        'libre' : !asiento.ocupado
                    };
                } else {
                    return {
                        'ocupada' : asiento.ocupado === 1 || asiento.ocupado === true,
                        'ocupada validado-control-acceso' :  (asiento.ocupado === 1 || asiento.ocupado === true) && asiento.info && asiento.info.info_acceso?.accedido === true,
                        'pendiente' : asiento.ocupado === 2 || asiento.info?.reserva_id,
                        'seleccionada' : asiento.seleccionado,
                        'libre' : asiento.ocupado === 0 || asiento.ocupado === false,
                        'bloqueada': asiento.ocupado === 3 && ! asiento.info?.reserva_id,
                        "abonado": (asiento.ocupado === 1 || asiento.ocupado === true) && asiento.info && (asiento.info.es_abonado || asiento.info.abonado_lista_abonado_id > 0) && asiento.info.n_abonado >= 0,
                    };
                }                
            },
            getTitle(asiento, fila) {
                if(this.front){
                    return 'Fila: ' + fila + ' Butaca: ' + asiento.asiento;
                }

                if(asiento.ocupado === 3 || (asiento.info && asiento.info.concepto != null)) {
                    if(asiento.info?.reserva_id) {
                        return 'Asiento reservado vinculado a un enlace de compra';
                    } else {
                        // Un bloqueo originado por un evento guarda el nombre del
                        // evento dentro del concepto ("Entrada evento: X"). Se
                        // traduce a una etiqueta fija para no exponer el evento.
                        return 'Concepto: ' + resolveConceptoBloqueo(asiento.info || {});
                    }                
                } else {
                    var text = '';

                    text = text + 'Fila: ' + fila + ' Butaca: ' + asiento.asiento;

                    if(asiento.info) {
                        if(asiento.info.n_abonado) {
                            text = text + ' Nº Abonado: ' + asiento.info.n_abonado;
                        }

                        if(asiento.info.name) {
                            text = text + '\nNombre: ' + asiento.info.name;
                        }

                        if(asiento.info.surnames) {
                            text = text + ' ' + asiento.info.surnames;
                        }
                    
                        if(asiento.info.tpv_order) {
                            text = text + '\nNº del pedido: ' + asiento.info.tpv_order
                        }

                        if(asiento.info.info_acceso?.accedido) {
                            text = text + '\nHora de acceso: ' + asiento.info.info_acceso.hora_acceso
                        }
                    
                        if(asiento.info.telefono) {
                            text = text + ' Telefono: ' + asiento.info.telefono
                        }

                    }

                    if(asiento.lfp_id) {
                        text = text + ' Lfp ID: ' + asiento.lfp_id;
                    }
                    return text;
                }
            },
            showContextMenu(event) {
                event.preventDefault();
                this.$emit('menu', event.clientY, event.clientX, this.asiento);
            },
            getButacaStyle(asiento, disabled) {
                let style = {
                    pointerEvents: disabled ? 'none' : 'auto',
                };

                if (asiento.info?.etiqueta_bloqueo?.color) {
                    style.backgroundColor = asiento.info.etiqueta_bloqueo.color;
                    style.color = 'white';
                }

                // Inline cursor override so the global `.butaca.bloqueada`
                // `cursor: not-allowed` rule only applies when the caller
                // has NOT opted-in. Inline styles win over the class
                // selector, so this is the surgical way to keep the purple
                // visual state while making the seat look actionable.
                if (this.bloqueadaPointerCursor && asiento.ocupado === 3) {
                    style.cursor = 'pointer';
                }

                return style;
            }
        },
    }
</script>

<style>
    .butaca {
        width: 16px;
        height: 15px;
        margin-right: 3px;
        margin-bottom: 1px;
        line-height: 2.3;
        border-radius: 0 0 4px 4px;
        cursor: pointer;
        text-align: center;
        display: inline-block;
        font-size: 7px;
        opacity: 0.8;
    }
    .butaca.libre {
        background: rgb(169, 209, 146);
    }
    .butaca.ocupada, .butaca.bloqueada, .butaca.pendiente, .butaca.oculto, .butaca.columna, .butaca.columna_n, .butaca.abonado {
        cursor: not-allowed;
    }
    .butaca.ocupada, .butaca.ocupada a {
        background-color: #CC0000;
        color: white;
    }
    .butaca.ocupada.validado-control-acceso{
        background-color: #01018a;
        color: white;
    }
    .butaca.seleccionada {
        background-color: #457ad1!important;
    }
    .butaca.hueco {
        background: transparent !important;
        cursor: default;
        border: none;
        pointer-events: none;
    }
    .butaca.pendiente {
        background-color: #EAAA00;
    }
    .butaca.bloqueada {
        background-color: #bf95c4;
    }
    .butaca.abonado {
        background-color: black !important;
    }
</style>
